import { Icon } from '@design/components/icon/Icon';
import { Spinner } from '@design/components/spinner/Spinner';
import { formatFileSize } from '@helpers/utils/file-size/file-size';
import {
  UploadedFile,
  UploadingStatus,
} from '@terminal/views/alfred/research/use-user-upload/use-user-upload.types';
import { useUserUpload } from '@terminal/views/alfred/research/use-user-upload/useUserUpload';
import {
  allowedFileTypes,
  imageTypes,
} from '@terminal/views/alfred/research/utils/formatUploadFiles';
import { useTranslation } from 'react-i18next';

import styles from './SingleFile.module.scss';

const imageMimeTypes = Object.values(imageTypes).map(type => type.mimeType);

const fileNameClassMap: Record<UploadingStatus, string> = {
  uploading: styles.fileNameUploading,
  complete: styles.fileNameComplete,
  error: styles.fileNameError,
};

const fileSizeClassMap: Record<UploadingStatus, string> = {
  uploading: styles.fileSizeUploading,
  complete: styles.fileSizeComplete,
  error: styles.fileSizeError,
};

interface SingleFileProps
  extends Omit<UploadedFile, 'id' | 'fileUrl' | 'fileData'> {
  editable?: boolean;
}

export const SingleFile = ({
  field_name,
  fileType,
  thumbnailUrl,
  name,
  status,
  sizeInBytes,
  errorMessage,
  editable = true,
}: SingleFileProps) => {
  const { t } = useTranslation(['chat']);

  const removeFile = useUserUpload(state => state.removeFile);

  const locales = {
    byte: t('chat:fileSizes.byte'),
    kilobyte: t('chat:fileSizes.kilobyte'),
    megabyte: t('chat:fileSizes.megabyte'),
    gigabyte: t('chat:fileSizes.gigabyte'),
    terabyte: t('chat:fileSizes.terabyte'),
  };

  const imageSrc = (() => {
    const fileTypeObj = Object.values(allowedFileTypes).find(
      config => config.mimeType === fileType
    );

    if (imageMimeTypes.includes(fileType)) {
      return thumbnailUrl ?? fileTypeObj?.icon;
    }

    return fileTypeObj?.icon ?? undefined;
  })();

  const shouldFadeImage =
    status !== 'complete' && !fileType.startsWith('image/');

  return (
    <div
      className={
        editable ? styles.singleFileRootEditable : styles.singleFileRoot
      }
    >
      <div className={styles.mainContent}>
        <div className={styles.previewWrapper}>
          <div className={styles.iconWrapper}>
            <div>
              {fileType.startsWith('image/') && status !== 'complete' && (
                <div
                  className={styles.greyOverlay}
                  data-testid="grey-overlay"
                />
              )}
              <img
                data-testid="file-icon"
                className={
                  shouldFadeImage ? styles.styledImageFaded : styles.styledImage
                }
                src={imageSrc}
                alt={name}
              />
            </div>
          </div>
          {status === 'uploading' && (
            <div className={styles.spinnerWrapper}>
              <Spinner size="small" />
            </div>
          )}
          {status === 'error' && (
            <Icon
              className={styles.styledErrorIcon}
              iconName="ExclamationCircle"
              size={16}
              fillColor="var(--icon-critical)"
            />
          )}
        </div>
        <div className={styles.fileInfoWrapper}>
          <span className={fileNameClassMap[status] ?? styles.fileNameComplete}>
            {name}
          </span>
          <span className={fileSizeClassMap[status] ?? styles.fileSizeComplete}>
            {formatFileSize({ sizeInBytes, decimals: 2, locales })}
            {status === 'error' && errorMessage && (
              <span className={styles.errorMessage} data-testid="error-message">
                {errorMessage}
              </span>
            )}
          </span>
        </div>
      </div>
      {editable && (
        <Icon
          className={styles.styledIcon}
          iconName="Close"
          onClick={() => {
            removeFile(field_name);
          }}
          size={12}
        />
      )}
    </div>
  );
};
